import { pantryColors, pantryTypography } from '@pantry/ui'
import { useFormikContext } from 'formik'
import React, { useMemo, useState } from 'react'
import { usePantryStore } from '../../../../../providers/PantryProvider'
import type { AppleWalletPassConfigInput } from '../../../../../types/loyalty'
import { GalleryPickerWithProvider } from '../../../../media/components/GalleryPickerWithProvider'
import { getFolderId, isFileImage } from '../../../utils/media'
import { RuleCard } from '../rules/RuleCard'

const p = pantryColors
const F = pantryTypography.fontFamily

const Icon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
)

type ImageFieldKey =
  | 'icon_url'
  | 'icon_2x_url'
  | 'icon_3x_url'
  | 'logo_url'
  | 'logo_2x_url'
  | 'logo_3x_url'

interface FieldDef {
  key: ImageFieldKey
  label: string
  helperText: string
  // Tile aspect rendered in the card. Icons are square; logos are wide.
  tile: { width: number; height: number }
}

const ICON_FIELDS: FieldDef[] = [
  {
    key: 'icon_url',
    label: 'Icon @1x',
    helperText: 'Exactly 29×29 PNG',
    tile: { width: 88, height: 88 },
  },
  {
    key: 'icon_2x_url',
    label: 'Icon @2x',
    helperText: 'Exactly 58×58 PNG',
    tile: { width: 88, height: 88 },
  },
  {
    key: 'icon_3x_url',
    label: 'Icon @3x',
    helperText: 'Exactly 87×87 PNG',
    tile: { width: 88, height: 88 },
  },
]

const LOGO_FIELDS: FieldDef[] = [
  {
    key: 'logo_url',
    label: 'Logo @1x',
    helperText: 'Up to 160×50 PNG',
    tile: { width: 160, height: 50 },
  },
  {
    key: 'logo_2x_url',
    label: 'Logo @2x',
    helperText: 'Up to 320×100 PNG',
    tile: { width: 160, height: 50 },
  },
  {
    key: 'logo_3x_url',
    label: 'Logo @3x',
    helperText: 'Up to 480×150 PNG',
    tile: { width: 160, height: 50 },
  },
]

const getMediaSrc = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object') return ''
  const v = value as { src?: unknown; public_url?: unknown; url?: unknown }
  if (typeof v.src === 'string') return v.src
  if (typeof v.public_url === 'string') return v.public_url
  if (typeof v.url === 'string') return v.url
  return ''
}

export const AppleWalletImagesCard: React.FC = () => {
  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<AppleWalletPassConfigInput>()
  const store = usePantryStore()
  const orgId = store?.orgId ?? ''

  // One shared GalleryPickerWithProvider instance — the `activeField` selector
  // routes the picked URL back to the right Formik field. Six independent
  // pickers would each mount their own Gallery provider tree, which is wasteful.
  const [activeField, setActiveField] = useState<ImageFieldKey | null>(null)

  const handleOpen = (key: ImageFieldKey) => () => setActiveField(key)
  const handleClose = () => setActiveField(null)

  const handleSave = (fileSelected: unknown[]) => {
    if (!activeField) return
    const picked = fileSelected?.[0]
    const url = getMediaSrc(picked)
    setFieldValue(activeField, url, true)
    if (!touched[activeField]) setFieldTouched(activeField, true, false)
    setActiveField(null)
  }

  const handleRemove = (key: ImageFieldKey) => () => {
    setFieldValue(key, '', true)
    if (!touched[key]) setFieldTouched(key, true, false)
  }

  // GalleryPicker expects an array of currently-selected media docs. Build the
  // single-element array from the active field's URL so the picker can mark it
  // as already-selected when the modal opens.
  const activeImages = useMemo(() => {
    if (!activeField) return []
    const url = values[activeField]
    return url ? [{ src: url, public_url: url }] : []
  }, [activeField, values])

  const renderTile = (def: FieldDef) => {
    const url = values[def.key]
    const src = getMediaSrc(url)
    const wasTouched = Boolean(touched[def.key])
    const errorMessage = wasTouched
      ? (errors[def.key] as string | undefined)
      : undefined
    return (
      <div
        key={def.key}
        // Lock the column to the tile's width so a long error message wraps
        // beneath the tile instead of stretching the row and pushing siblings
        // around. Logos are wider than icons, so this width is per-field.
        style={{
          width: def.tile.width,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: p.gray[700],
          }}
        >
          {def.label}
        </div>
        {/*
          Tile + remove-button sit in a position:relative wrapper so the
          remove ✕ can overlay the tile's top-right corner without nesting
          a <button> inside a <button> (invalid HTML).
        */}
        <div
          style={{
            position: 'relative',
            width: def.tile.width,
            height: def.tile.height,
          }}
        >
          <button
            type="button"
            onClick={handleOpen(def.key)}
            title={src ? `Change ${def.label}` : `Upload ${def.label}`}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: 8,
              border: `1px solid ${errorMessage ? '#DC2626' : p.gray[200]}`,
              background: src
                ? `${p.gray[50]} url("${src}") center / contain no-repeat`
                : p.gray[50],
              color: p.gray[400],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 4,
              cursor: 'pointer',
              fontFamily: F,
              padding: 0,
            }}
          >
            {!src && (
              <>
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  width="16"
                  height="16"
                >
                  <rect x="1.5" y="2.5" width="13" height="11" rx="1.8" />
                  <circle cx="5" cy="6" r="1.2" />
                  <path d="M1.7 11l3.3-2.8 2.4 2.4 1.9-1.9 4.8 4.6" />
                </svg>
                <span style={{ fontSize: 11, fontWeight: 500 }}>Upload</span>
              </>
            )}
          </button>
          {src && (
            <button
              type="button"
              onClick={handleRemove(def.key)}
              title={`Remove ${def.label}`}
              aria-label={`Remove ${def.label}`}
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: `1px solid ${p.gray[200]}`,
                background: p.white,
                color: p.gray[600],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                fontFamily: F,
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              >
                <path d="M2 2l6 6M8 2l-6 6" />
              </svg>
            </button>
          )}
        </div>
        <div
          style={{
            fontSize: 11,
            color: errorMessage ? '#DC2626' : p.gray[400],
            fontWeight: errorMessage ? 500 : 400,
            lineHeight: 1.4,
            // Error messages can run long ("Image must be exactly 87×87
            // (got 1024×1024)"); allow them to wrap onto multiple lines
            // inside the tile column rather than overflowing horizontally.
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            width: '100%',
          }}
        >
          {errorMessage ?? def.helperText}
        </div>
      </div>
    )
  }

  return (
    <RuleCard title="Apple Wallet Images" icon={<Icon />}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          padding: '6px 0',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: p.gray[500],
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 10,
            }}
          >
            Icons
          </div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            }}
          >
            {ICON_FIELDS.map(renderTile)}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: p.gray[500],
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 10,
            }}
          >
            Logos
          </div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            }}
          >
            {LOGO_FIELDS.map(renderTile)}
          </div>
        </div>
      </div>

      <GalleryPickerWithProvider
        orgId={orgId}
        getFolderId={getFolderId as (folderId: string) => string}
        isFileImage={isFileImage as (file: unknown) => boolean}
        open={activeField !== null}
        onClose={handleClose}
        title={
          activeField
            ? `Select ${
                [...ICON_FIELDS, ...LOGO_FIELDS].find(
                  (f) => f.key === activeField,
                )?.label ?? 'image'
              }`
            : 'Select image'
        }
        images={activeImages}
        handleSave={handleSave}
        multiple={false}
        noUploadVideo
      />
    </RuleCard>
  )
}
